/**
 * Multi-specialty foundation: departments (specialty/routing), encounter templates
 * (specialty-driven extra vitals/note fields), and the two proof-of-concept specialties
 * (gynecology/obstetrics, pediatrics). See medopd-backend's Department/EncounterTemplate entities.
 */
import { fetchApi } from '../client';

export interface DepartmentDto {
  id: string;
  name: string;
  code: string;
  description?: string;
  templateKey?: string | null;
  headDoctorId?: string;
  displayOrder: number;
  isActive: boolean;
}

export interface SpecialtyKeyOption {
  key: string;
  label: string;
}

export interface EncounterFieldSchema {
  key: string;
  label: string;
  type: 'number' | 'text' | 'select' | 'date';
  unit?: string;
  options?: string[];
}

export interface EncounterTemplateDto {
  templateKey: string;
  visitType: string;
  label: string;
  vitalsExtraFields: EncounterFieldSchema[];
  noteExtraFields: EncounterFieldSchema[];
}

export const departmentsModule = {
  list: (activeOnly = true) =>
    fetchApi<DepartmentDto[]>(`/api/admin/departments?activeOnly=${activeOnly}`),
  specialtyKeys: () =>
    fetchApi<SpecialtyKeyOption[]>('/api/admin/departments/specialty-keys'),
  create: (body: Partial<DepartmentDto>) =>
    fetchApi<DepartmentDto>('/api/admin/departments', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: string, body: Partial<DepartmentDto>) =>
    fetchApi<DepartmentDto>(`/api/admin/departments/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deactivate: (id: string) =>
    fetchApi<{ message: string }>(`/api/admin/departments/${id}`, { method: 'DELETE' }),
};

export const encounterTemplatesModule = {
  /** Returns null (in `.data`) when there's no specialty template — render the generic form. */
  resolve: (departmentId: string | null | undefined, visitType: string) =>
    fetchApi<EncounterTemplateDto | null>(
      `/api/encounter-templates/resolve?visitType=${visitType}${departmentId ? `&departmentId=${departmentId}` : ''}`
    ),
};

export interface ObstetricProfileDto {
  id?: string;
  patientId?: string;
  gravida?: number;
  para?: number;
  lastMenstrualPeriod?: string;
  expectedDeliveryDate?: string;
  gestationalAgeWeeks?: number;
  highRisk?: boolean;
  notes?: string;
}

export const obstetricModule = {
  getProfile: (patientId: string) =>
    fetchApi<ObstetricProfileDto>(`/api/obstetric/patients/${patientId}/profile`),
  saveProfile: (patientId: string, body: ObstetricProfileDto) =>
    fetchApi<ObstetricProfileDto>(`/api/obstetric/patients/${patientId}/profile`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
};

export interface ImmunizationDto {
  id: string;
  vaccineCode: string;
  vaccineName: string;
  doseNumber: number;
  dueDate?: string;
  administeredDate?: string;
  batchNumber?: string;
  status: 'DUE' | 'ADMINISTERED' | 'MISSED' | 'SKIPPED';
}

export interface GrowthPercentileResult {
  ageMonths: number;
  weightKg: number;
  zScore: number;
  percentile: number;
  disclaimer: string;
}

export const pediatricModule = {
  listImmunizations: (patientId: string) =>
    fetchApi<ImmunizationDto[]>(`/api/pediatric/patients/${patientId}/immunizations`),
  initializeImmunizationSchedule: (patientId: string) =>
    fetchApi<ImmunizationDto[]>(`/api/pediatric/patients/${patientId}/immunizations/initialize`, { method: 'POST' }),
  administerDose: (patientId: string, immunizationId: string, body: { administeredDate?: string; batchNumber?: string; notes?: string }) =>
    fetchApi<ImmunizationDto>(`/api/pediatric/patients/${patientId}/immunizations/${immunizationId}/administer`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  growthPercentile: (patientId: string, weightKg?: number) =>
    fetchApi<GrowthPercentileResult>(
      `/api/pediatric/patients/${patientId}/growth-percentile${weightKg ? `?weightKg=${weightKg}` : ''}`
    ),
};
